/**
 * QuotationFormDrawer — premium two-column quotation builder.
 *
 * Layout on desktop (≥ lg):
 *   ┌─────────────────────────────────┬────────────────────┐
 *   │ Patient · Doctor (2-col)        │  Sticky summary    │
 *   │ Treatment cards + Add btn       │  Subtotal          │
 *   │ Discount · Validity (2-col)     │  Discount          │
 *   │ Notes (collapsible)             │  GST               │
 *   │                                 │  Grand total       │
 *   └─────────────────────────────────┴────────────────────┘
 * Mobile: stacks vertically. Summary docks under the treatment list.
 * Footer: sticky — Cancel · Save Draft · Preview PDF · Save & Send.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, Pencil, X, Search, Loader2, Sparkles, Check,
  User as UserIcon, Send, FileDown, Save, ClipboardCheck, ChevronDown,
  Info, StickyNote,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { DateDMYInput } from "@/components/DateDMYInput";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;

// Stable per-line key generator. Item order is user-editable and lines can be
// removed / imported / patched in place, so we can't safely use array index
// as a React key — that would let a removed row's DOM (and its inputs' focus /
// draft state) get "adopted" by the next row down. Every item gets a unique
// `_key` at construction time and we use that as the React key.
let _lineSeq = 0;
const nextKey = () => `qt-line-${++_lineSeq}-${Date.now().toString(36)}`;

const emptyItem = () => ({
  _key: nextKey(),
  name: "", amount: 0, quantity: 1, tooth: "",
  discount_pct: 0, procedure_id: null,
});

const VALIDITY_PRESETS = [
  { days: 7,  label: "7 days" },
  { days: 15, label: "15 days" },
  { days: 30, label: "30 days" },
  { days: 60, label: "60 days" },
];
const DISCOUNT_CHIPS = [5, 10, 15, 20];

function isoPlusDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + Number(days));
  return d.toISOString().slice(0, 10);
}
function ageFromDob(dob) {
  if (!dob) return null;
  try {
    const d = new Date(dob);
    const now = new Date();
    let a = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1;
    return a >= 0 && a < 130 ? a : null;
  } catch { return null; }
}


export default function QuotationFormDrawer({ open, quotationId, onOpenChange, onSaved }) {
  const { user } = useAuth();
  const isEdit = !!quotationId;

  const [patient, setPatient] = useState(null);
  const [doctorId, setDoctorId] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [items, setItems] = useState([]);
  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState(0);
  const [gstRate, setGstRate] = useState(0);
  const [notes, setNotes] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [validUntil, setValidUntil] = useState(isoPlusDays(30));
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [existingNumber, setExistingNumber] = useState(null);
  const [status, setStatus] = useState("draft");

  // Add-treatment dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [editIdx, setEditIdx] = useState(null); // null | index

  // Bootstrap doctor list + procedure catalog once per open
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [d1, d2] = await Promise.all([
          api.get("/team").catch(() => ({ data: [] })),
          api.get("/procedure-catalog").catch(() => ({ data: [] })),
        ]);
        setDoctors((d1.data || []).filter(u => u.role === "doctor" || u.role === "admin"));
        setCatalog(d2.data || []);
      } catch { /* silent */ }
    })();
  }, [open]);

  // Reset / hydrate on open
  useEffect(() => {
    if (!open) return;
    if (!quotationId) {
      setPatient(null);
      setDoctorId(user?.id || null);
      setItems([]);
      setDiscountType("none"); setDiscountValue(0);
      setGstRate(0);
      setNotes(""); setNotesOpen(false);
      setValidUntil(isoPlusDays(30));
      setStatus("draft"); setExistingNumber(null);
      return;
    }
    setLoading(true);
    api.get(`/quotations/${quotationId}`).then(({ data }) => {
      const q = data.quotation;
      setExistingNumber(q.quotation_number);
      setPatient(data.patient);
      setDoctorId(q.doctor_id);
      setItems((q.items || []).map(it => ({ ...emptyItem(), ...it })));
      setDiscountType(q.discount_type || "none");
      setDiscountValue(q.discount_value || 0);
      setGstRate(q.gst_rate || 0);
      setNotes(q.notes || "");
      setNotesOpen(!!q.notes);
      setValidUntil((q.valid_until || "").slice(0, 10));
      setStatus(q.status || "draft");
    }).catch(e => toast.error(apiError(e, "Could not load quotation")))
      .finally(() => setLoading(false));
  }, [open, quotationId, user?.id]);

  // Live totals — mirror backend `_compute_invoice_totals` sans place-of-supply
  const totals = useMemo(() => {
    const subtotal = items.reduce((s, it) =>
      s + (Number(it.amount) || 0) * (Number(it.quantity) || 1), 0);
    let discount = 0;
    if (discountType === "percentage") discount = subtotal * (Number(discountValue) || 0) / 100;
    else if (discountType === "fixed") discount = Math.min(Number(discountValue) || 0, subtotal);
    const taxable = Math.max(subtotal - discount, 0);
    const tax = taxable * (Number(gstRate) || 0) / 100;
    return {
      subtotal: round(subtotal),
      discount: round(discount),
      taxable: round(taxable),
      tax: round(tax),
      total: round(taxable + tax),
    };
  }, [items, discountType, discountValue, gstRate]);

  // -------------------- actions --------------------
  const addOrReplaceItem = (patch) => {
    if (editIdx !== null) {
      setItems(items.map((x, i) => i === editIdx ? { ...x, ...patch } : x));
    } else {
      setItems([...items, { ...emptyItem(), ...patch }]);
    }
    setAddOpen(false);
    setEditIdx(null);
  };
  const removeItem = (idx) =>
    setItems(items.filter((_, i) => i !== idx));

  const importFromTreatmentPlan = async () => {
    if (!patient) { toast.error("Select a patient first"); return; }
    const tid = toast.loading("Loading planned treatments…");
    try {
      const { data } = await api.get(`/treatments?patient_id=${patient.id}`);
      const planned = (data || []).filter(t =>
        (t.status === "planned" || t.status === "in_progress" || !t.status) && !t.invoice_id
      );
      if (planned.length === 0) {
        toast.info("No planned treatments to import for this patient.", { id: tid });
        return;
      }
      const imported = planned.map(t => ({
        ...emptyItem(),
        name: t.procedure || "Procedure",
        amount: Number(t.cost || 0),
        quantity: 1,
        tooth: t.tooth || "",
        procedure_id: t.procedure_id || null,
      }));
      setItems(prev => [...prev, ...imported]);
      toast.success(`Imported ${imported.length} treatment${imported.length > 1 ? "s" : ""}`, { id: tid });
    } catch (e) {
      toast.error(apiError(e, "Import failed"), { id: tid });
    }
  };

  const save = async ({ afterStatus } = {}) => {
    if (!patient) { toast.error("Please select a patient"); return; }
    const cleanItems = items.filter(it => it.name && Number(it.amount) > 0);
    if (cleanItems.length === 0) { toast.error("Add at least one treatment"); return; }
    setSaving(true);
    try {
      const payload = {
        patient_id: patient.id,
        doctor_id: doctorId,
        items: cleanItems.map(it => ({
          name: it.name,
          amount: Number(it.amount),
          quantity: Number(it.quantity) || 1,
          tooth: it.tooth || null,
          procedure_id: it.procedure_id || null,
        })),
        discount_type: discountType,
        discount_value: Number(discountValue) || 0,
        gst_rate: Number(gstRate) || 0,
        notes: notes || null,
        valid_until: validUntil ? new Date(validUntil).toISOString() : null,
        status: afterStatus || (isEdit ? undefined : "draft"),
      };
      let saved;
      if (isEdit) {
        const { data } = await api.patch(`/quotations/${quotationId}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/quotations", payload);
        saved = data;
      }
      if (afterStatus === "sent" && !isEdit) {
        await api.post(`/quotations/${saved.id}/send`, { channels: [] });
      }
      toast.success(isEdit ? "Quotation updated" : `Quotation ${saved.quotation_number} created`);
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err, "Save failed"));
    } finally { setSaving(false); }
  };

  const previewPdf = async () => {
    if (!isEdit) { toast.info("Save the draft first to preview the PDF"); return; }
    try {
      const resp = await api.get(`/quotations/${quotationId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("PDF preview failed"); }
  };

  const singleDoctor = doctors.length === 1;
  const selectedDoctor = doctors.find(d => d.id === doctorId);

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          overlayClassName="bg-ink/40 backdrop-blur-sm"
          className="w-full sm:max-w-3xl p-0 flex flex-col bg-linen overflow-hidden"
          data-testid="quotation-form-drawer"
        >
          {/* Header */}
          <SheetHeader className="px-6 pt-5 pb-4 bg-white border-b border-[#EFEADC] sticky top-0 z-10">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="overline text-ink-soft inline-flex items-center gap-1.5">
                  <ClipboardCheck className="w-3 h-3" />
                  {isEdit ? "Edit quotation" : "New quotation"}
                </div>
                <SheetTitle className="font-display text-2xl text-ink mt-0.5 tracking-tight truncate">
                  {existingNumber || "Treatment estimate"}
                </SheetTitle>
                <div className="text-xs text-ink-mid mt-0.5">Optional. For high-value or planned treatments.</div>
              </div>
              {status !== "draft" && (
                <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border font-medium bg-[#EEF0FA] text-[#3C4A8A] border-[#C7CFEB]">
                  {status}
                </span>
              )}
            </div>
          </SheetHeader>

          {/* Body */}
          {loading ? (
            <div className="p-8 text-sm text-ink-mid inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading quotation…
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              <div className="p-6 grid lg:grid-cols-[1fr_260px] gap-6">
                {/* -------------------- Left column -------------------- */}
                <div className="space-y-6 min-w-0">
                  {/* Patient · Doctor (2-col) */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Section title="Patient" testid="qt-section-patient">
                      {patient ? (
                        <PatientCard patient={patient} onChange={() => setPatient(null)} />
                      ) : (
                        <PatientSearchInput onSelect={setPatient} />
                      )}
                    </Section>
                    <Section title="Doctor" testid="qt-section-doctor">
                      {singleDoctor || (isEdit && !doctors.length) ? (
                        <ReadOnlyField
                          value={selectedDoctor?.name || user?.name || "Unassigned"}
                          hint="Auto-selected"
                        />
                      ) : (
                        <select
                          data-testid="qt-doctor"
                          value={doctorId || ""}
                          onChange={(e) => setDoctorId(e.target.value || null)}
                          className="w-full rounded-xl border border-[#E8EAE9] px-3 h-[42px] text-sm bg-white"
                        >
                          <option value="">Unassigned</option>
                          {doctors.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.name}{d.role === "admin" ? " (Admin)" : ""}
                            </option>
                          ))}
                        </select>
                      )}
                    </Section>
                  </div>

                  {/* Treatment builder */}
                  <div>
                    <div className="overline text-ink-soft mb-2">Treatments</div>
                    {/* Both secondary actions live on ONE row above the list */}
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={() => { setEditIdx(null); setAddOpen(true); }}
                        data-testid="qt-add-btn"
                        className="text-[11px] px-3 py-1.5 rounded-full border border-pine/30 text-pine bg-white hover:bg-pine/10 inline-flex items-center gap-1.5"
                      >
                        <Plus className="w-3 h-3" /> Add treatment
                      </button>
                      <button
                        onClick={importFromTreatmentPlan}
                        disabled={!patient}
                        data-testid="qt-import-plan"
                        className="text-[11px] px-3 py-1.5 rounded-full border border-[#E8EAE9] text-ink-mid bg-white hover:bg-linen inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                        title={patient ? "Import planned treatments for this patient" : "Select a patient first"}
                      >
                        <Sparkles className="w-3 h-3" />
                        Import from treatment plan
                      </button>
                    </div>

                    {items.length === 0 ? (
                      <EmptyTreatmentState onAdd={() => { setEditIdx(null); setAddOpen(true); }} />
                    ) : (
                      <div className="space-y-2" data-testid="qt-items-list">
                        {items.map((it, idx) => (
                          <TreatmentCard
                            key={it._key || `qt-line-${idx}`}
                            item={it}
                            onEdit={() => { setEditIdx(idx); setAddOpen(true); }}
                            onRemove={() => removeItem(idx)}
                            testid={`qt-item-card-${idx}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Discount · GST (2-col) */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Section title="Discount" testid="qt-section-discount">
                      <DiscountControl
                        type={discountType} value={discountValue}
                        onType={setDiscountType} onValue={setDiscountValue}
                      />
                    </Section>
                    <Section title="GST rate" testid="qt-section-gst">
                      <div className="flex items-center gap-2">
                        <input
                          data-testid="qt-gst-rate"
                          type="number" min={0} max={28} step={0.5}
                          value={gstRate}
                          onChange={(e) => setGstRate(e.target.value)}
                          className="flex-1 rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm bg-white"
                          placeholder="0"
                        />
                        <span className="text-xs text-ink-soft">%</span>
                      </div>
                    </Section>
                  </div>

                  {/* Validity full-width — chips then date picker */}
                  <Section title="Validity" testid="qt-section-validity">
                    <ValidityControl value={validUntil} onChange={setValidUntil} />
                  </Section>

                  {/* Notes full-width — collapsed by default */}
                  <NotesSection open={notesOpen} onOpen={setNotesOpen} value={notes} onChange={setNotes} />
                </div>

                {/* -------------------- Right column: sticky summary -------------------- */}
                <aside className="lg:sticky lg:top-4 self-start" data-testid="qt-summary">
                  <div className="rounded-2xl bg-white border border-[#EFEADC] p-4 space-y-2">
                    <div className="overline text-ink-soft">Summary</div>
                    <TotalRow label="Subtotal" v={totals.subtotal} />
                    {totals.discount > 0 && (
                      <TotalRow label="Discount" v={-totals.discount} muted />
                    )}
                    {totals.tax > 0 && (
                      <TotalRow label={`GST @ ${gstRate}%`} v={totals.tax} muted />
                    )}
                    <div className="border-t border-[#F0EBE0] my-1" />
                    <div className="flex items-baseline justify-between">
                      <span className="text-ink font-medium text-sm">Grand total</span>
                      <span data-testid="qt-total" className="font-display text-2xl tabular-nums text-pine">
                        {INR(totals.total)}
                      </span>
                    </div>
                    <div className="text-[10px] text-ink-soft pt-1">
                      {items.length} item{items.length === 1 ? "" : "s"} · valid until {validUntil || "—"}
                    </div>
                  </div>
                  <div className="mt-3 text-[10px] text-ink-soft leading-relaxed px-1">
                    <Info className="w-3 h-3 inline mr-1 align-[-1px]" />
                    This is an estimate. Prices are indicative and never touch the ledger until an invoice is created.
                  </div>
                </aside>
              </div>
            </div>
          )}

          {/* Sticky footer */}
          <div className="sticky bottom-0 bg-white border-t border-[#EFEADC] px-6 py-3 flex items-center gap-2 justify-end flex-wrap">
            <button
              onClick={() => onOpenChange(false)}
              className="text-xs px-3 py-2 rounded-full border border-[#E8EAE9] text-ink-mid hover:bg-linen"
              data-testid="qt-cancel"
            >
              Cancel
            </button>
            {isEdit && (
              <button
                onClick={previewPdf}
                className="text-xs px-3 py-2 rounded-full border border-[#E8EAE9] text-ink-mid hover:bg-linen inline-flex items-center gap-1.5"
                data-testid="qt-preview-pdf"
              >
                <FileDown className="w-3.5 h-3.5" /> Preview PDF
              </button>
            )}
            <button
              onClick={() => save({})}
              disabled={saving}
              className="text-xs px-3 py-2 rounded-full border border-pine/30 text-pine bg-white hover:bg-pine/10 inline-flex items-center gap-1.5"
              data-testid="qt-save-draft"
            >
              <Save className="w-3.5 h-3.5" /> {isEdit ? "Save changes" : "Save draft"}
            </button>
            <button
              onClick={() => save({ afterStatus: isEdit ? undefined : "sent" })}
              disabled={saving}
              className="btn-primary text-xs inline-flex items-center gap-1.5 py-2"
              data-testid="qt-save-and-send"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {isEdit ? "Save & close" : "Save & send"}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <AddTreatmentDialog
        open={addOpen}
        onOpenChange={(v) => { setAddOpen(v); if (!v) setEditIdx(null); }}
        catalog={catalog}
        initial={editIdx !== null ? items[editIdx] : null}
        onSave={addOrReplaceItem}
      />
    </>
  );
}


/* ==================================================================== */
/* Sub-components                                                       */
/* ==================================================================== */

function PatientCard({ patient, onChange }) {
  const age = ageFromDob(patient.date_of_birth);
  return (
    <div
      className="rounded-xl bg-white border border-[#E8EAE9] px-3 h-[42px] flex items-center gap-2.5"
      data-testid="qt-patient-card"
    >
      <div className="w-7 h-7 rounded-full bg-pine/10 grid place-items-center shrink-0">
        <UserIcon className="w-3.5 h-3.5 text-pine" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-ink truncate leading-tight">{patient.name}</div>
        <div className="text-[10px] text-ink-soft font-mono truncate leading-tight">
          {patient.phone || "—"}
          {age !== null && ` · ${age} yr`}
          {patient.gender && ` · ${String(patient.gender)[0].toUpperCase()}${String(patient.gender).slice(1).toLowerCase()}`}
        </div>
      </div>
      <button
        onClick={onChange}
        data-testid="qt-patient-change"
        className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-[#E8EAE9] text-ink-mid hover:bg-linen shrink-0"
      >
        Change
      </button>
    </div>
  );
}

function ReadOnlyField({ value, hint }) {
  return (
    <div className="rounded-xl bg-white border border-[#E8EAE9] px-3 h-[42px] flex items-center justify-between"
         data-testid="qt-doctor-readonly">
      <div className="text-sm text-ink truncate">{value}</div>
      {hint && <div className="text-[10px] text-ink-soft uppercase tracking-widest shrink-0 ml-2">{hint}</div>}
    </div>
  );
}

function EmptyTreatmentState({ onAdd }) {
  return (
    <div
      className="rounded-xl bg-white border border-dashed border-[#D6D2C3] px-4 py-3.5 flex items-center gap-3"
      data-testid="qt-empty-state"
    >
      <div className="w-8 h-8 rounded-full bg-linen grid place-items-center shrink-0">
        <ClipboardCheck className="w-4 h-4 text-ink-soft" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink font-medium">No treatment added yet.</div>
        <div className="text-[11px] text-ink-soft mt-0.5">Click "Add treatment" above to begin.</div>
      </div>
      <button
        onClick={onAdd}
        data-testid="qt-add-first"
        className="btn-primary inline-flex items-center gap-1.5 text-[11px] py-1.5 px-3 shrink-0"
      >
        <Plus className="w-3 h-3" /> Add treatment
      </button>
    </div>
  );
}

function TreatmentCard({ item, onEdit, onRemove, testid }) {
  const qty = Number(item.quantity) || 1;
  const price = Number(item.amount) || 0;
  const lineDiscountPct = Number(item.discount_pct) || 0;
  const gross = qty * price;
  const lineDisc = gross * lineDiscountPct / 100;
  const lineTotal = gross - lineDisc;

  return (
    <div
      className="rounded-2xl bg-white border border-[#EFEADC] px-4 py-3 flex items-start gap-3"
      data-testid={testid}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink font-medium truncate">{item.name || "Untitled procedure"}</div>
        <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-ink-mid">
          {item.tooth && <MetaChip>Tooth {item.tooth}</MetaChip>}
          <MetaChip>Qty {qty}</MetaChip>
          <MetaChip>{INR(price)} / unit</MetaChip>
          {lineDiscountPct > 0 && <MetaChip>−{lineDiscountPct}%</MetaChip>}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-display text-lg tabular-nums text-ink">{INR(lineTotal)}</div>
        <div className="flex items-center gap-1 mt-1 justify-end">
          <button
            onClick={onEdit}
            className="w-7 h-7 rounded-md border border-[#EFEADC] text-ink-mid hover:bg-linen grid place-items-center"
            title="Edit"
            data-testid={`${testid}-edit`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onRemove}
            className="w-7 h-7 rounded-md border border-[#EFEADC] text-ink-mid hover:bg-red-50 hover:text-red-600 grid place-items-center"
            title="Remove"
            data-testid={`${testid}-remove`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function MetaChip({ children }) {
  return (
    <span className="px-2 py-0.5 rounded-full bg-linen/70 border border-[#EFEADC] text-[10px] text-ink-mid">
      {children}
    </span>
  );
}


function DiscountControl({ type, value, onType, onValue }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <select
          data-testid="qt-discount-type"
          value={type}
          onChange={(e) => onType(e.target.value)}
          className="rounded-xl border border-[#E8EAE9] px-2.5 py-2 text-xs bg-white"
        >
          <option value="none">None</option>
          <option value="percentage">%</option>
          <option value="fixed">₹</option>
        </select>
        <input
          data-testid="qt-discount-value"
          type="number" min={0}
          value={value}
          onChange={(e) => onValue(e.target.value)}
          disabled={type === "none"}
          className="flex-1 rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm bg-white disabled:bg-[#F5F1E5]"
          placeholder="0"
        />
      </div>
      {type === "percentage" && (
        <div className="flex items-center gap-1 flex-wrap">
          {DISCOUNT_CHIPS.map((pct) => (
            <button
              key={pct}
              type="button"
              onClick={() => onValue(pct)}
              data-testid={`qt-discount-chip-${pct}`}
              className={`text-[11px] px-2.5 py-1 rounded-full border ${
                Number(value) === pct
                  ? "bg-pine text-linen border-pine"
                  : "bg-white text-ink-mid border-[#E8EAE9] hover:bg-linen"
              }`}
            >
              {pct}%
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ValidityControl({ value, onChange }) {
  // Which preset (if any) matches the current selection?
  const activePreset = useMemo(() => {
    if (!value) return null;
    for (const p of VALIDITY_PRESETS) {
      if (isoPlusDays(p.days) === value) return p.days;
    }
    return null;
  }, [value]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 flex-wrap">
        {VALIDITY_PRESETS.map((p) => (
          <button
            key={p.days}
            type="button"
            onClick={() => onChange(isoPlusDays(p.days))}
            data-testid={`qt-validity-chip-${p.days}`}
            className={`text-[11px] px-2.5 py-1 rounded-full border ${
              activePreset === p.days
                ? "bg-pine text-linen border-pine"
                : "bg-white text-ink-mid border-[#E8EAE9] hover:bg-linen"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <DateDMYInput
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="qt-valid-until"
        className="w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm bg-white"
      />
    </div>
  );
}

function NotesSection({ open, onOpen, value, onChange }) {
  return (
    <div>
      <button
        type="button"
        onClick={() => onOpen(!open)}
        data-testid="qt-notes-toggle"
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-white border border-[#EFEADC] hover:bg-linen/40 text-sm text-ink-mid"
      >
        <span className="inline-flex items-center gap-2">
          <StickyNote className="w-3.5 h-3.5" />
          Notes {value ? <span className="text-[10px] text-ink-soft ml-1">· added</span> : null}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <textarea
          data-testid="qt-notes"
          value={value} onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder="e.g. Multiple visits, warranty details, sterilisation included…"
          className="mt-2 w-full rounded-xl border border-[#E8EAE9] px-3 py-2.5 text-sm bg-white resize-none"
        />
      )}
    </div>
  );
}


/* ==================================================================== */
/* Add-treatment dialog                                                 */
/* ==================================================================== */
function AddTreatmentDialog({ open, onOpenChange, catalog, initial, onSave }) {
  const isEdit = !!initial;
  const [step, setStep] = useState(isEdit ? "edit" : "pick"); // pick | edit
  const [draft, setDraft] = useState(initial || emptyItem());
  const [needle, setNeedle] = useState("");

  useEffect(() => {
    if (open) {
      setStep(initial ? "edit" : "pick");
      setDraft(initial || emptyItem());
      setNeedle("");
    }
  }, [open, initial]);

  const filtered = useMemo(() => {
    const n = needle.toLowerCase().trim();
    if (!n) return catalog.slice(0, 40);
    return catalog.filter((p) =>
      (p.name || "").toLowerCase().includes(n) ||
      (p.category || "").toLowerCase().includes(n) ||
      (p.variant || "").toLowerCase().includes(n),
    ).slice(0, 40);
  }, [needle, catalog]);

  const lineTotal = useMemo(() => {
    const qty = Number(draft.quantity) || 1;
    const price = Number(draft.amount) || 0;
    const disc = (qty * price) * (Number(draft.discount_pct) || 0) / 100;
    return qty * price - disc;
  }, [draft]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="qt-add-treatment-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#EFEADC]">
          <DialogTitle className="font-display text-lg text-ink">
            {isEdit ? "Edit treatment" : "Add treatment"}
          </DialogTitle>
          <div className="text-xs text-ink-soft mt-1">
            {step === "pick"
              ? "Import from your treatment master, or type a custom procedure."
              : "Set tooth number, quantity, unit price, and optional line discount."}
          </div>
        </DialogHeader>

        {step === "pick" ? (
          <>
            <div className="px-5 py-3 border-b border-[#F5F1E5]">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
                <input
                  autoFocus
                  value={needle}
                  onChange={(e) => setNeedle(e.target.value)}
                  placeholder="Search — e.g. Root canal, Implant, Aligners…"
                  className="w-full pl-9 pr-3 py-2 rounded-xl border border-[#E8EAE9] text-sm bg-white"
                  data-testid="qt-add-search"
                />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto divide-y divide-[#F5F1E5]" data-testid="qt-add-results">
              {filtered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setDraft({
                      ...emptyItem(),
                      name: p.name,
                      amount: Number(p.default_price || 0),
                      procedure_id: p.id,
                    });
                    setStep("edit");
                  }}
                  className="w-full text-left px-5 py-3 hover:bg-linen/60 flex items-center justify-between gap-3"
                  data-testid={`qt-add-option-${p.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm text-ink truncate">{p.name}{p.variant ? ` · ${p.variant}` : ""}</div>
                    <div className="text-[10px] text-ink-soft uppercase tracking-widest mt-0.5">
                      {p.category || "General"}
                    </div>
                  </div>
                  <div className="text-xs text-ink-mid tabular-nums shrink-0">
                    ₹{Number(p.default_price || 0).toLocaleString("en-IN")}
                  </div>
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="px-5 py-6 text-center text-xs text-ink-soft">
                  No procedure matches "{needle}"
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-[#EFEADC] bg-linen/40">
              <button
                type="button"
                onClick={() => {
                  setDraft({ ...emptyItem(), name: needle || "Custom procedure" });
                  setStep("edit");
                }}
                data-testid="qt-add-custom"
                className="text-xs text-pine underline hover:no-underline"
              >
                {needle
                  ? `Add "${needle}" as a custom procedure →`
                  : "Add a custom procedure →"}
              </button>
            </div>
          </>
        ) : (
          <div className="p-5 space-y-3">
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Treatment name"
              className="w-full rounded-xl border border-[#E8EAE9] px-3 py-2.5 text-sm bg-white"
              data-testid="qt-draft-name"
            />
            <div className="grid grid-cols-3 gap-2">
              <LabelledInput label="Tooth" value={draft.tooth}
                             onChange={(v) => setDraft({ ...draft, tooth: v })}
                             placeholder="e.g. 11"
                             testid="qt-draft-tooth" />
              <LabelledInput label="Quantity" value={draft.quantity} type="number" min={1}
                             onChange={(v) => setDraft({ ...draft, quantity: v })}
                             testid="qt-draft-qty" />
              <LabelledInput label="Unit price ₹" value={draft.amount} type="number" min={0}
                             onChange={(v) => setDraft({ ...draft, amount: v })}
                             testid="qt-draft-price" />
            </div>
            <LabelledInput label="Line discount %" value={draft.discount_pct || 0} type="number" min={0} max={100}
                           onChange={(v) => setDraft({ ...draft, discount_pct: v })}
                           testid="qt-draft-line-discount" />
            <div className="flex items-center justify-between pt-2 border-t border-[#F5F1E5]">
              <div className="text-xs text-ink-soft uppercase tracking-widest">Line total</div>
              <div className="font-display text-xl tabular-nums text-pine" data-testid="qt-draft-line-total">
                {INR(lineTotal)}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              {!isEdit && (
                <button
                  type="button"
                  onClick={() => setStep("pick")}
                  className="text-xs px-3 py-2 rounded-full border border-[#E8EAE9] text-ink-mid hover:bg-linen"
                  data-testid="qt-draft-back"
                >
                  Back
                </button>
              )}
              <button
                type="button"
                onClick={() => onSave(draft)}
                disabled={!draft.name || !(Number(draft.amount) > 0)}
                className="btn-primary text-xs inline-flex items-center gap-1.5 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="qt-draft-save"
              >
                <Check className="w-3.5 h-3.5" />
                {isEdit ? "Update treatment" : "Add treatment"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ==================================================================== */
/* Patient search                                                       */
/* ==================================================================== */
function PatientSearchInput({ onSelect }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const s = q.trim();
    if (!s) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/patients");
        const needle = s.toLowerCase();
        setResults((data || []).filter((p) =>
          (p.name || "").toLowerCase().includes(needle) ||
          (p.phone || "").toLowerCase().includes(needle) ||
          (p.email || "").toLowerCase().includes(needle)
        ).slice(0, 6));
      } catch { /* silent */ } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div>
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
        <input
          value={q}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search patient by name, phone, or email…"
          className="w-full pl-9 pr-3 h-[42px] rounded-xl border border-[#E8EAE9] text-sm bg-white"
          data-testid="qt-patient-search"
        />
      </div>
      {focused && (loading || results.length > 0) && (
        <div
          className="mt-2 rounded-xl bg-white border border-[#EFEADC] divide-y divide-[#F5F1E5] max-h-64 overflow-y-auto"
          data-testid="qt-patient-results"
        >
          {loading && (
            <div className="px-3 py-2 text-xs text-ink-soft">Searching…</div>
          )}
          {!loading && results.map((p) => {
            const age = ageFromDob(p.date_of_birth);
            return (
              <button
                key={p.id}
                onClick={() => onSelect(p)}
                className="w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-linen/60"
                data-testid={`qt-patient-result-${p.id}`}
              >
                <div className="w-8 h-8 rounded-full bg-pine/10 grid place-items-center shrink-0">
                  <UserIcon className="w-3.5 h-3.5 text-pine" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-ink truncate">{p.name}</div>
                  <div className="text-[10px] text-ink-soft mt-0.5 font-mono truncate">
                    {p.phone || "—"}{age !== null && ` · ${age} yr`}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* -------------------- primitives -------------------- */
function Section({ title, testid, children }) {
  return (
    <section data-testid={testid}>
      <div className="overline text-ink-soft mb-1.5">{title}</div>
      {children}
    </section>
  );
}
function LabelledInput({ label, value, onChange, type = "text", min, max, placeholder, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">{label}</div>
      <input
        type={type} value={value}
        min={min} max={max}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm bg-white"
        data-testid={testid}
      />
    </label>
  );
}
function TotalRow({ label, v, muted }) {
  return (
    <div className={`flex items-baseline justify-between text-sm ${muted ? "text-ink-mid" : "text-ink"}`}>
      <span>{label}</span>
      <span className="font-mono tabular-nums">{INR(v)}</span>
    </div>
  );
}
function round(n) { return Math.round((Number(n) || 0) * 100) / 100; }
